import { ResolverTag } from '@ps/web-core-enums';
import { pickBy } from 'lodash';
import URL, { qs } from 'url-parse';

import type { EffectParams } from '@swf/core/store';
import { getState, select } from '@swf/core/store';
import type {
  CasinoGame,
  CasinoGameInstance,
  CasinoTable,
  JoinReason,
  PlayerFSB,
  Properties
} from '@swf/core/types/CasinoGame';
import { toSignedHex } from '@swf/core/utils';
import { closeWindow, getUGCImages } from '@swf/core/utils/casino';

const open = async (
  {
    instance,
    game,
    windowRef,
    joinReason,
    fsb,
    widgetId
  }: {
    instance: CasinoGameInstance;
    game?: CasinoGame;
    windowRef?: Window | null;
    joinReason: JoinReason;
    fsb?: PlayerFSB;
    widgetId?: string;
  },
  params: EffectParams
) => {
  const { rootState, dispatch } = params;
  const cookieDomain = select.location.getCookieDomain(rootState);
  const isMobile = select.device.isMobile(rootState);
  const hostId = select.casino.getHostId(rootState);
  const { casinoappDomainSource } = select.config.get(rootState);
  const { properties = {}, featureNames = [] } = (game?.tables[0] as CasinoTable) || {};
  const {
    lines = '',
    holdRate = 0,
    multiplier = false,
    wilds = false
  } = (properties as Properties) || {};
  const { tags = [] } = game || {};

  if (select.account.isMigrationRequired(rootState)) {
    dispatch.modal.enqueue({ path: '/account/migration' });
    closeWindow(windowRef, isMobile);
    return;
  }

  instance.cur =
    instance.cur === 'XXX' ? (fsb ? fsb.currency : select.currency.get(rootState)) : instance.cur;
  delete instance.xtk;

  const ugcImages = getUGCImages(game);

  if (select.device.isEmbedded(rootState)) {
    const { token, signature, userWebId } = select.session.getLegacySession(rootState);
    const params = pickBy(
      {
        c: select.account.getCountry(rootState),
        cur: instance.cur,
        fsbId: instance.fsbId,
        ftrs: toSignedHex(instance.ftrs),
        gt: instance.gType,
        hostId,
        launchKey: instance.launchKey,
        maxbet: instance.maxBet,
        mbin: instance.mbin,
        minbet: instance.minBet,
        ms: instance.maxS,
        pff: instance.pFF,
        pt: instance.pType,
        sa: instance.sAddr,
        sn: instance.sNum >= 0 ? instance.sNum : -1,
        so: instance.sInst,
        tb: instance.tId,
        tbn: instance.tNm,
        tcr: joinReason,
        tt: instance.ttId,
        ufgid: instance.ufgId,
        uid: instance.uid,
        v: instance.vId,
        vt: instance.vType,
        vtn: instance.vName,
        ...ugcImages,
        SBTK: token,
        SBSG: signature,
        WBID: userWebId
      },
      (value) => typeof value !== 'undefined'
    );
    window?.starsweb?.casino.launch(qs.stringify(params));
  } else if (windowRef) {
    isMobile &&
      windowRef &&
      widgetId &&
      windowRef.history.pushState(null, '', `#SW${widgetId?.replace(/.*\//, '')}`);
    await dispatch.resolver.fetchTag({
      tag: ResolverTag.StarsWebMinilobby,
      query: {
        site: select.cms.getSiteName(rootState).toLowerCase(),
        pathPlatform: isMobile ? 'mobileweb' : 'web',
        nano: 2,
        home: windowRef.location.href,
        lm: toSignedHex(instance.lm),
        _jr: joinReason,
        currency: instance.cur,
        _rtable: JSON.stringify({
          ...instance,
          lines,
          holdRate,
          multiplier,
          wilds,
          featureNames: featureNames?.join() ?? '',
          tags: tags?.join() ?? ''
        }),
        hostId,
        ...ugcImages,
        loginPlatform: select.casino.getClientPlatform(rootState)
      }
    });
    const state = getState();
    const { url } = select.resolver.getTag(state, ResolverTag.StarsWebMinilobby);
    const parsed = new URL(url);
    if (casinoappDomainSource !== 'resolver') {
      const [prefix] = parsed.hostname.split('.');
      parsed.set('hostname', `${prefix}.${cookieDomain}`);
    }
    windowRef.location.href = parsed.toString();
  }
};

export default open;
